import { useMutation } from '@tanstack/react-query'
import { CircleTickIcon } from '@web/components/atoms/Icons'
import { Center, Stack } from '@web/components/atoms/Layout'
import { Body2, Section2 } from '@web/components/atoms/Typography'
import { ExtraFields, useDonationFormContext } from '@web/components/page-components/DonationForm'
import useGraphQLClient from '../../../libs/hooks/useGraphQLClient'
import { useEffect, useMemo } from 'react'
import { graphql } from '@web/graphql-queries/index'
import { QUICK_PAY_PAYMENT_METHOD_TYPES, usePaymentStore } from '../../../libs/hooks/usePaymentStore'
import { CommentInput, PostCommentMutation } from '@web/graphql-queries/graphql'
import { PageButton } from '@web/components/page-components/PageButton'
import { DonationFormStepProps, EXCLUDED_FIELDS, useInstallmentAmount, useTotalAmountWithDonorPays } from './utils'
import { useShallow } from 'zustand/react/shallow'
import { usePageStore } from '../../../libs/hooks/usePageStore'
import { useMergeFields } from '@web/components/page-components/Text/MergeTags/MergeTag'
import { useSubmitQuickPayValuesMutation } from 'apps/fe-donation/libs/queries'
import { toDisplayAmount } from '@web/components/utils/format'
import { AddressInput, CustomFieldElementType, ExtraField, ExtraFieldType } from '@web/graphql-types'
import { DONOR_MERGE_TAGS, GIVING_MERGE_TAGS } from '@web/components/page-components/Text/MergeTags/utils'
import NameForm from '@web/components/page-components/DonationForm/components/molecules/NameForm'
import { Nullable } from 'apps/fe-donation/utils'
import ProfileSelector from './ProfileSelector'
import { Spinner } from '@web/components/atoms/Spinner'
import { useDonationFormStore } from '@web/components/page-components/DonationForm/DonationFormProvider'
import { usePathname, useSearchParams } from 'next/navigation'
import { DonationFrequency } from '@web/graphql-components'
import { DONATION_FREQUENCY_TO_LABEL } from '@web/components/page-components/DonationForm/donationUtils'
import { usePurchaseCopy } from '@web/components/page-components/DonationForm/purchaseCopy'

const postCommentDocument = graphql(/* GraphQL */ `
  mutation PostComment($input: CommentInput!) {
    postComment(comment: $input)
  }
`)

type OnSubmitValues = {
  transactionId: string
  charityId: string
  organisation?: string
  firstName: string
  lastName: string
  address?: Nullable<AddressInput>
  fullAddress?: string
  phone?: string
}

const [FIRST_NAME_TAG, LAST_NAME_TAG] = DONOR_MERGE_TAGS
const [, AMOUNT_TAG, , FREQUENCY_TAG] = GIVING_MERGE_TAGS

const DonationFormFinalStep = ({
  extraFields: extraFieldsSettings,
  styles,
  onSubmit,
  onFinalise,
}: DonationFormStepProps & { onSubmit?: (input: OnSubmitValues) => void; onFinalise?: () => Promise<void> }) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
  const thanksRoute = `${pathname === '/' ? '' : pathname}/thanks${query}`
  const {
    extraFields,
    address,
    givingFrequency = DonationFrequency.OneTime,
    isPayByInstallments,
  } = useDonationFormContext(useShallow(({ formValues }) => formValues))
  const store = useDonationFormStore()
  const isEmbed = usePageStore((state) => state.isEmbed)
  const { firstName, lastName, organisation } = extraFields ?? {}
  const amount = useTotalAmountWithDonorPays()
  const installmentAmount = useInstallmentAmount()
  const amountToUse = isPayByInstallments ? installmentAmount : amount
  const purchaseCopy = usePurchaseCopy()
  const fields: ExtraField[] = useMemo(
    () => extraFieldsSettings.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [extraFieldsSettings],
  )

  const paymentTransactionId = usePaymentStore((store) => store.paymentTransactionId)
  const paymentSubscriptionId = usePaymentStore((store) => store.paymentSubscriptionId)
  const paymentMethodType = usePaymentStore((store) => store.paymentMethodType)

  const { profileId } = usePageStore.getState()

  const isThreeStepForm = styles?.steps === 3
  const isQuickPay = paymentMethodType && QUICK_PAY_PAYMENT_METHOD_TYPES.includes(paymentMethodType)
  const isCollectingMore = !isThreeStepForm && isQuickPay

  const setMergeField = useMergeFields((state) => state.setMergeField)

  const graphQLClient = useGraphQLClient()
  const {
    mutateAsync: postComment,
    isPending: isLoading,
    isSuccess,
  } = useMutation({
    mutationFn: (input: CommentInput) =>
      graphQLClient<PostCommentMutation>({
        document: postCommentDocument,
        options: {
          input,
        },
      }),
  })
  const { mutateAsync: submitQuickPayValues } = useSubmitQuickPayValuesMutation()

  const sendCommentApi = async () => {
    const { charityId, isLiveMode } = usePageStore.getState()

    await postComment({
      paymentTransactionId: paymentTransactionId === '' ? null : paymentTransactionId,
      paymentSubscriptionId: paymentSubscriptionId === '' ? null : paymentSubscriptionId,
      message: extraFields?.comment ?? '',
      fullName: firstName?.trim() + ' ' + lastName?.trim(),
      isPIIPublic: extraFields?.isAnonymous !== 'true',
      charityId,
      isLiveMode,
      profileId,
      dedicateeName: extraFields?.dedicateeName?.trim() ?? null,
      dedicateeEmail: extraFields?.dedicateeEmail?.trim() ?? null,
      organisationName: organisation?.trim() ?? null,
    })
  }

  const redirectToThanks = () => {
    if (firstName) {
      setMergeField(FIRST_NAME_TAG.MergeFieldSettingId, firstName)
    }

    if (lastName) {
      setMergeField(LAST_NAME_TAG.MergeFieldSettingId, lastName)
    }

    setMergeField('Giving.Amount', toDisplayAmount(amountToUse))
    setMergeField(AMOUNT_TAG.MergeFieldSettingId, toDisplayAmount(amountToUse))
    setMergeField(AMOUNT_TAG.MergeFieldSettingId.toLowerCase(), toDisplayAmount(amountToUse))
    setMergeField(FREQUENCY_TAG.MergeFieldSettingId, DONATION_FREQUENCY_TO_LABEL[givingFrequency])
    usePageStore.setState({ amount: Number(amountToUse), donationFrequency: givingFrequency })
    if (!pathname.endsWith('/thanks')) {
      if (!isEmbed) {
        window.history.pushState(null, '', thanksRoute)
      }
      usePageStore.setState({ isThankYouPage: true })
    }
    window.scrollTo(0, 0)
  }

  const processAndRedirect = async () => {
    await Promise.all([sendCommentApi(), onFinalise ? onFinalise() : Promise.resolve()])

    const formFields = store.getState().formValues.extraFields || {}
    const { address: fullAddress, mobile, comment, isAnonymous, email, ...customFields } = formFields

    const customFieldInputs = Object.entries(customFields)
      .map(([key, value]: [string, string]) => {
        const customField = fields.find((field) => field.customField?.id === key)
        let values = [value?.trim()]

        if (
          customField?.type === ExtraFieldType.Custom &&
          customField.customField?.elementType === CustomFieldElementType.MultiSelect
        ) {
          values = values[0].split(',')
        }

        return {
          customFieldSettingId: key,
          name: (customField?.name ?? '')?.trim(),
          values,
        }
      })
      .filter(({ name }) => !EXCLUDED_FIELDS.includes(name))

    const fieldValues = {
      transactionId: paymentTransactionId ?? '',
      charityId: usePageStore.getState().charityId,
      firstName: firstName?.trim(),
      lastName: lastName?.trim(),
      organisation: organisation?.trim(),
      address,
      fullAddress,
      phone: mobile?.trim(),
      customFields: customFieldInputs,
    }
    if (onSubmit) {
      if (firstName) {
        setMergeField(FIRST_NAME_TAG.MergeFieldSettingId, firstName)
      }

      if (lastName) {
        setMergeField(LAST_NAME_TAG.MergeFieldSettingId, lastName)
      }

      onSubmit(fieldValues)
    }

    if (isThreeStepForm && isQuickPay) {
      try {
        await submitQuickPayValues(fieldValues)
      } catch (e) {
        console.warn(e)
      }
    }

    var customFieldValues = {}
    customFieldInputs.forEach((x) => {
      const fieldName = x.name
        .trim()
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .replace(' ', '_')
        .toLowerCase()
      customFieldValues[fieldName] = x.values.join(',')
    })

    usePageStore.getState().actions.setCustomFieldValues({
      customFieldValues,
    })

    redirectToThanks()
  }

  useEffect(() => {
    if (onSubmit) {
      return
    }

    if (isCollectingMore) {
      usePaymentStore.setState({ showDataCollectionPopup: !isEmbed })
      redirectToThanks()

      return
    }

    processAndRedirect()
  }, [])
  return (
    <Stack direction="column" w="100%">
      <Center>
        <Stack direction="row" alignItems="center">
          {isCollectingMore ? <CircleTickIcon boxSize="24px" /> : <Spinner size="sm" />}
          <Section2 data-testid="donation-thanks">
            {firstName ? `${firstName}, t` : 'T'}hanks for your ${toDisplayAmount(amountToUse)}{' '}
            {givingFrequency !== DonationFrequency.OneTime ? `${DONATION_FREQUENCY_TO_LABEL[givingFrequency]} ` : ''}
            {purchaseCopy('donation')}
          </Section2>
        </Stack>
      </Center>
      <Center>
        <Body2>
          Please check your email for your tax-deductible receipt
          {isCollectingMore && ' and review & confirm your details below.'}
        </Body2>
      </Center>
      {isCollectingMore && (
        <Stack
          direction="column"
          w="100%"
          as="form"
          onSubmit={(e) => {
            e.preventDefault()
            processAndRedirect()
          }}
        >
          <NameForm />
          <ExtraFields fields={fields} Components={{ ProfileSelector }} />
          <PageButton
            name="Primary"
            type="submit"
            buttonText={isSuccess ? 'Details updated' : 'Confirm'}
            isLoading={isLoading}
            bgColorOverride={styles?.accentColor}
          />
        </Stack>
      )}
    </Stack>
  )
}

export default DonationFormFinalStep
